/**
 * LegacyDeepLinkRedirect — RW-2299.
 *
 * RW-1524 retired the legacy Coming Soon + Buyer Needs modules; the Market
 * boards own those surfaces (under /connect since RW-3647). The old paths are still in the wild — every
 * match / celebration / status email and in-app notification sent before
 * RW-2299 carries one, and users have them bookmarked. The redirect used to be
 * a bare string, so the record id was dropped and every deep link landed on
 * the board's first page.
 *
 * The redirect KEEPS the id now, in every legacy form:
 *
 *   /coming-soon/:id          → /connect/coming-soon/:id
 *   /coming-soon?listing=:id  → /connect/coming-soon/:id     (also ?id=)
 *   /buyer-needs/:id          → /connect/buyer-needs/:id
 *   /buyer-needs?profile=:id  → /connect/buyer-needs/:id     (also ?id=)
 *   /coming-soon, /buyer-needs (bare) → the boards, unchanged
 *
 * RW-3647 (Clint, 2026-10-21) filed both boards under Connect, so they moved
 * from /deals/* to /connect/*. Every notification, email and appLinks.js link
 * sent before that carries /deals/…, often with board state in the query
 * (?status=, ?chips=, ?edit=) — so these keep the WHOLE tail, query and hash:
 *
 *   /deals/coming-soon[/…][?…][#…] → /connect/coming-soon[/…][?…][#…]
 *   /deals/buyer-needs[/…][?…][#…] → /connect/buyer-needs[/…][?…][#…]
 *
 * RW-2917 retired the one-item Tools section; Email Signatures moved to
 * Company › /email-signatures. The reply-signature reminder, the certificate
 * alert and emails already sent link to /tools/signatures/…, so those keep
 * their sub-page AND query (Install's ?app=gmail):
 *
 *   /tools, /tools/signatures       → /email-signatures
 *   /tools/signatures/install?app=x → /email-signatures/install?app=x
 *
 * The boards read that trailing id (DealsComingSoon / DealsBuyerNeeds):
 * highlight the card when it is on the loaded feed, otherwise hand off to the
 * record page. Nothing else reads a legacy query key, so it is not carried.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const LEGACY = [
  { base: '/coming-soon', target: '/connect/coming-soon', keys: ['listing', 'id'] },
  { base: '/buyer-needs', target: '/connect/buyer-needs', keys: ['profile', 'id'] },
];

/** RW-3647: the boards' pre-move home → their /connect home, tail kept verbatim. */
const MOVED = [
  { from: '/deals/coming-soon', to: '/connect/coming-soon' },
  { from: '/deals/buyer-needs', to: '/connect/buyer-needs' },
];

/**
 * The /connect path a legacy Coming Soon / Buyer Needs URL redirects to, or
 * null when the location is not a legacy path. Pure — App.js and the tests
 * both go through here.
 */
export function legacyDeepLinkTarget(pathname, search = '', hash = '') {
  const path = String(pathname || '');
  for (const { from, to } of MOVED) {
    if (path === from || path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}${search || ''}${hash || ''}`;
  }
  if (path === '/tools' || path === '/tools/' || path === '/tools/signatures' || path.startsWith('/tools/signatures/')) {
    const rest = path.startsWith('/tools/signatures/') ? path.slice('/tools/signatures'.length).replace(/\/+$/, '') : '';
    return `/email-signatures${rest}${search || ''}`;
  }
  for (const { base, target, keys } of LEGACY) {
    if (path !== base && !path.startsWith(`${base}/`)) continue;
    const fromPath = path.slice(base.length + 1).split('/')[0];
    // A malformed legacy URL (/coming-soon/100%) must not throw during App
    // render — the 404 guard calls this too. Mirror boardFocusId's guard.
    let id = '';
    if (fromPath) { try { id = decodeURIComponent(fromPath); } catch { id = fromPath; } }
    if (!id) {
      const params = new URLSearchParams(search || '');
      for (const k of keys) {
        const v = (params.get(k) || '').trim();
        if (v) { id = v; break; }
      }
    }
    return id ? `${target}/${encodeURIComponent(id)}` : target;
  }
  return null;
}

/** Renders a replace-navigation for a legacy path; nothing otherwise. */
export default function LegacyDeepLinkRedirect() {
  const { pathname, search, hash } = useLocation();
  const target = legacyDeepLinkTarget(pathname, search, hash);
  return target ? <Navigate to={target} replace /> : null;
}
